import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Ship, MapPin, Calendar, Anchor, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TrackWidget from "../components/TrackWidget";
import { api } from "../lib/api";

export default function Track() {
    const [params] = useSearchParams();
    const cn = params.get("cn");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!cn) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setErr(null);
            try {
                const r = await api.get(`/track/${encodeURIComponent(cn)}`);
                if (!cancelled) setData(r.data);
            } catch (e) {
                if (!cancelled) setErr(e?.response?.data?.detail || "Unable to fetch tracking");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [cn]);

    return (
        <div data-testid="track-page" className="bg-[#0A0A0E] text-zinc-100 min-h-screen">
            <Navbar />
            <main className="pt-28 pb-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-cyan-300 mb-6" data-testid="track-back-link">
                    <ArrowLeft size={14} /> Back to home
                </Link>

                <div className="mb-8">
                    <span className="section-tag">Track · Live preview</span>
                    <h1 className="font-display text-4xl sm:text-5xl text-white mt-5 leading-tight">
                        Track your container.
                    </h1>
                    <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">
                        Drop in any container number. No login, no integration. This is a
                        preview connected to demo data — the production platform plugs into
                        live carrier feeds.
                    </p>
                </div>

                <div className="card-glow rounded-2xl p-5 sm:p-7 mb-10">
                    <TrackWidget compact />
                </div>

                {loading && (
                    <div className="flex items-center gap-3 text-zinc-400" data-testid="track-loading">
                        <Loader2 size={16} className="animate-spin text-cyan-300" />
                        Fetching tracking data...
                    </div>
                )}

                {err && (
                    <div className="card-glow rounded-xl p-5 flex items-start gap-3 border-red-400/30" data-testid="track-error">
                        <AlertCircle size={18} className="text-red-400 mt-0.5" />
                        <div>
                            <div className="text-white font-medium">Could not load tracking</div>
                            <div className="text-sm text-zinc-400 mt-1">{err}</div>
                        </div>
                    </div>
                )}

                {data && !loading && (
                    <div data-testid="track-result">
                        {/* Header card */}
                        <div className="card-glow rounded-2xl p-6 sm:p-8 mb-6">
                            <div className="flex flex-wrap items-start justify-between gap-6">
                                <div>
                                    <div className="eyebrow">Container · {data.container_number}</div>
                                    <h2 className="font-display text-2xl sm:text-3xl text-white mt-3">
                                        {data.current_status}
                                    </h2>
                                    <div className="grid sm:grid-cols-3 gap-5 mt-6 text-sm">
                                        <Field icon={<Anchor size={14} />} label="Carrier" value={data.carrier} />
                                        <Field icon={<Ship size={14} />} label="Vessel" value={data.vessel} />
                                        <Field icon={<Calendar size={14} />} label="ETA" value={formatDate(data.eta)} />
                                        <Field icon={<MapPin size={14} />} label="Origin" value={data.origin} />
                                        <Field icon={<MapPin size={14} />} label="Destination" value={data.destination} />
                                    </div>
                                </div>
                                <ProgressRing pct={data.progress_percent} />
                            </div>
                            {data.mocked && (
                                <div className="mt-6 pt-5 border-t border-white/5 flex items-start gap-2 text-[11px] font-mono-accent text-zinc-500 uppercase tracking-[0.15em]">
                                    <AlertCircle size={12} className="text-amber-400 mt-0.5 flex-shrink-0" />
                                    {data.disclaimer}
                                </div>
                            )}
                        </div>

                        {/* Timeline */}
                        <div className="card-glow rounded-2xl p-6 sm:p-8">
                            <div className="eyebrow mb-6">Shipment timeline</div>
                            <ol className="relative space-y-7">
                                <span
                                    aria-hidden
                                    className="absolute left-[7px] top-2 bottom-2 w-px"
                                    style={{
                                        background: "linear-gradient(180deg, rgba(0,240,255,0.4), rgba(255,255,255,0.05))",
                                    }}
                                />
                                {data.events.slice().reverse().map((ev, i) => (
                                    <li key={i} className="flex gap-4 relative" data-testid={`track-event-${i}`}>
                                        <div className="relative z-10 mt-1.5">
                                            {ev.is_current ? (
                                                <span className="block w-4 h-4 rounded-full bg-cyan-400 ring-4 ring-cyan-400/20" />
                                            ) : (
                                                <span className="block w-4 h-4 rounded-full bg-zinc-700 ring-4 ring-zinc-700/20" />
                                            )}
                                        </div>
                                        <div>
                                            <div className={`font-display ${ev.is_current ? "text-cyan-300" : "text-white"} text-base sm:text-lg`}>
                                                {ev.status}
                                                {ev.is_current && (
                                                    <span className="ml-3 text-[10px] font-mono-accent uppercase tracking-[0.2em] text-cyan-400">
                                                        Current
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-sm text-zinc-400 mt-1 flex items-center gap-2">
                                                <MapPin size={12} /> {ev.location}
                                            </div>
                                            <div className="text-xs font-mono-accent text-zinc-500 mt-1">
                                                {formatDate(ev.timestamp)}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        <div className="mt-8 text-center">
                            <Link to="/#early-access" className="btn-primary" data-testid="track-cta-join">
                                Want this across all your shipments? <CheckCircle2 size={16} />
                            </Link>
                        </div>
                    </div>
                )}

                {!cn && !data && !loading && (
                    <div className="text-zinc-500 text-sm" data-testid="track-empty">
                        Enter a container number above to see a live demo trace.
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}

function Field({ icon, label, value }) {
    return (
        <div>
            <div className="text-[11px] font-mono-accent uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-2">
                <span className="text-cyan-300">{icon}</span>
                {label}
            </div>
            <div className="text-zinc-100 mt-1.5">{value}</div>
        </div>
    );
}

function ProgressRing({ pct }) {
    const r = 38;
    const c = 2 * Math.PI * r;
    const offset = c - (pct / 100) * c;
    return (
        <div className="relative w-24 h-24 flex-shrink-0">
            <svg width="96" height="96" viewBox="0 0 96 96" className="-rotate-90">
                <circle cx="48" cy="48" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
                <circle
                    cx="48"
                    cy="48"
                    r={r}
                    stroke="#00F0FF"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray={c}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 700ms ease" }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-display text-xl text-white">{pct}%</div>
                <div className="text-[9px] font-mono-accent text-zinc-500 tracking-[0.15em]">DONE</div>
            </div>
        </div>
    );
}

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
